import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useAddresses } from '../context/AddressContext';
import api from '../api';
import './BankStatements.css';

// The worker is copied into public/ at install time by scripts/copy-pdf-worker.js
// so it always matches the pdfjs-dist version that react-pdf depends on.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const ANNOTATION_KINDS = [
  { suffix: 'first month\u2019s rent', shortKey: 'first-month' },
  { suffix: 'security deposit', shortKey: 'security' },
];

const PAGE_WIDTH = 900;
// Half-axes of the annotation circle, in CSS pixels. Must match the
// width/height in BankStatements.css (.annotation-circle).
const CIRCLE_RX = 35;
const CIRCLE_RY = 18;

export default function BankStatements() {
  const { id: packageId } = useParams();
  const { addresses } = useAddresses();
  const [statements, setStatements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [annotations, setAnnotations] = useState([]);
  const [pending, setPending] = useState(null); // { page, x, y } during selection
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savingWorksheet, setSavingWorksheet] = useState(false);
  const [savedFiles, setSavedFiles] = useState([]);
  const fileRef = useRef();
  const saveTimer = useRef(null);

  const validAddresses = addresses.filter(a => a.trim());

  // Full list of address-derived options. Filtered against the annotations
  // already on the current statement so each preset can only be used once;
  // deleting an annotation puts its option back in the dropdown.
  const labelOptions = useMemo(() => {
    const used = new Set(annotations.map(a => a.label));
    const opts = [];
    for (const addr of validAddresses) {
      const street = addr.split(',')[0].trim();
      for (const kind of ANNOTATION_KINDS) {
        const value = `${street} ${kind.suffix}`;
        if (used.has(value)) continue;
        opts.push({ value, label: value });
      }
    }
    return opts;
  }, [validAddresses, annotations]);

  // Load list of statements for this refi pack
  const loadStatements = useCallback(async () => {
    try {
      const res = await api.get('/bank-statements', { params: { packageId } });
      setStatements(res.data.statements || []);
    } catch (err) {
      console.error('Failed to load statements:', err);
    }
  }, [packageId]);

  useEffect(() => {
    loadStatements();
  }, [loadStatements]);

  // Auto-select the most recent statement if nothing is selected yet
  useEffect(() => {
    if (!selectedId && statements.length > 0) {
      setSelectedId(statements[0].id);
    }
  }, [statements, selectedId]);

  // Load list of saved (annotated) bank statement PDFs for the sidebar
  const loadSavedFiles = useCallback(async () => {
    try {
      const res = await api.get('/bank-statements/files', { params: { packageId } });
      setSavedFiles(res.data.files || []);
    } catch (err) {
      console.error('Failed to load saved bank statements:', err);
    }
  }, [packageId]);

  useEffect(() => {
    loadSavedFiles();
  }, [loadSavedFiles]);

  // When selection changes, fetch the PDF blob via authenticated axios and load
  // its annotations from the already-loaded list
  useEffect(() => {
    if (!selectedId) {
      setPdfData(null);
      setAnnotations([]);
      setNumPages(0);
      return;
    }
    let cancelled = false;
    setPdfLoading(true);
    setPending(null);
    (async () => {
      try {
        const res = await api.get(`/bank-statements/${selectedId}/file`, { responseType: 'arraybuffer' });
        if (cancelled) return;
        // react-pdf reuses arraybuffers across renders, so wrap in a Uint8Array
        setPdfData({ data: new Uint8Array(res.data) });
        const meta = statements.find(s => s.id === selectedId);
        setAnnotations(meta?.annotations || []);
      } catch (err) {
        if (!cancelled) setError('Failed to load PDF.');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, statements]);

  const persistAnnotations = useCallback((next) => {
    if (!selectedId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/bank-statements/${selectedId}/annotations`, { annotations: next })
        .catch(err => console.error('Save annotations failed:', err));
    }, 500);
  }, [selectedId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('packageId', packageId);
      const res = await api.post('/bank-statements/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadStatements();
      setSelectedId(res.data.statement.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (statementId) => {
    if (!window.confirm('Delete this bank statement and all its annotations?')) return;
    try {
      await api.delete(`/bank-statements/${statementId}`);
      if (selectedId === statementId) setSelectedId(null);
      await loadStatements();
    } catch {
      setError('Failed to delete.');
    }
  };

  const handlePageClick = (pageNumber, e) => {
    if (pending) return; // ignore while a popover is open
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || y < 0 || x > 1 || y > 1) return;
    setPending({ page: pageNumber, x, y });
  };

  const handlePickLabel = (label) => {
    if (!pending) return;
    const ann = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      page: pending.page,
      x: pending.x,
      y: pending.y,
      // labelDx/labelDy are the label's offset from the circle, as a ratio
      // of page width/height. Default: 8% to the right, same row.
      labelDx: 0.08,
      labelDy: 0,
      label,
    };
    const next = [...annotations, ann];
    setAnnotations(next);
    persistAnnotations(next);
    setPending(null);
  };

  const updateAnnotation = (annId, patch) => {
    setAnnotations(prev => {
      const next = prev.map(a => a.id === annId ? { ...a, ...patch } : a);
      persistAnnotations(next);
      return next;
    });
  };

  const handleDeleteAnnotation = (annId) => {
    const next = annotations.filter(a => a.id !== annId);
    setAnnotations(next);
    persistAnnotations(next);
  };

  const handleSaveToWorksheet = async () => {
    if (!selectedId) return;
    setError('');
    setSavingWorksheet(true);
    try {
      // Flush any pending annotation auto-save first so the server has the
      // latest data before it bakes the PDF
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
        await api.put(`/bank-statements/${selectedId}/annotations`, { annotations });
      }
      await api.post(`/bank-statements/${selectedId}/save-annotated`);
      await loadSavedFiles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save annotated PDF.');
    } finally {
      setSavingWorksheet(false);
    }
  };

  const handleDownloadSaved = async (file) => {
    try {
      const res = await api.get(`/bank-statements/files/${file.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download saved file.');
    }
  };

  const handleDeleteSaved = async (file) => {
    if (!window.confirm(`Delete saved annotated PDF "${file.file_name}"?`)) return;
    try {
      await api.delete(`/bank-statements/files/${file.id}`);
      await loadSavedFiles();
    } catch {
      setError('Failed to delete saved file.');
    }
  };

  const cancelPending = () => setPending(null);

  // Close pending popover on Escape
  useEffect(() => {
    if (!pending) return;
    const onKey = (e) => { if (e.key === 'Escape') cancelPending(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  return (
    <div className="bank-statements-page">
      <h1 className="page-title">Bank Statements</h1>

      <div className="bank-toolbar">
        <button
          className="btn-primary"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Bank Statement'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          onChange={handleUpload}
          style={{ display: 'none' }}
        />
        {validAddresses.length === 0 && (
          <span className="toolbar-warning">Add addresses on the Addresses tab to enable annotation labels.</span>
        )}
      </div>

      {error && <div className="error-box">{error}</div>}

      {statements.length > 0 && (
        <div className="statement-tabs">
          {statements.map(s => (
            <div
              key={s.id}
              className={`statement-tab ${selectedId === s.id ? 'active' : ''}`}
              onClick={() => setSelectedId(s.id)}
            >
              <span className="statement-tab-name">{s.file_name}</span>
              <button
                className="btn-icon btn-icon-danger"
                onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
                title="Delete"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="bank-layout">
        <div className="bank-main">
          {!selectedId && statements.length === 0 && (
            <div className="empty-state">Upload a bank statement to get started.</div>
          )}

          {selectedId && (
            <>
              <div className="pdf-viewer-wrapper">
                {pdfLoading && <div className="pdf-status">Loading PDF...</div>}
                {pdfData && (
                  <Document
                    file={pdfData}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={(err) => setError('Failed to render PDF: ' + err.message)}
                    loading={<div className="pdf-status">Loading PDF...</div>}
                  >
                    {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNumber => (
                      <PdfPage
                        key={pageNumber}
                        pageNumber={pageNumber}
                        annotations={annotations.filter(a => a.page === pageNumber)}
                        pending={pending && pending.page === pageNumber ? pending : null}
                        labelOptions={labelOptions}
                        onClick={handlePageClick}
                        onPickLabel={handlePickLabel}
                        onCancelPending={cancelPending}
                        onDeleteAnnotation={handleDeleteAnnotation}
                        onUpdateAnnotation={updateAnnotation}
                      />
                    ))}
                  </Document>
                )}
              </div>
              <div className="bank-actions">
                <button
                  className="btn-primary"
                  onClick={handleSaveToWorksheet}
                  disabled={savingWorksheet || !pdfData}
                >
                  {savingWorksheet ? 'Saving...' : 'Save to Worksheet'}
                </button>
              </div>
            </>
          )}
        </div>

        <aside className="bank-sidebar">
          <h2 className="sidebar-title">Annotated Bank Statements for this Refi Pack</h2>
          {savedFiles.length === 0 ? (
            <p className="sidebar-empty">No saved annotated statements yet. Annotate a statement and click "Save to Worksheet".</p>
          ) : (
            <ul className="saved-list">
              {savedFiles.map(f => (
                <li key={f.id} className="saved-item">
                  <div className="saved-info">
                    <div className="saved-name">{f.file_name}</div>
                    <div className="saved-meta">Saved {new Date(f.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="saved-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleDownloadSaved(f)}
                      title="Download"
                    >
                      ↓
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => handleDeleteSaved(f)}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

function PdfPage({
  pageNumber,
  annotations,
  pending,
  labelOptions,
  onClick,
  onPickLabel,
  onCancelPending,
  onDeleteAnnotation,
  onUpdateAnnotation,
}) {
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);

  const handleBackgroundClick = (e) => {
    onClick(pageNumber, e);
  };

  // Compute the start point of a leader line: the intersection of the
  // line from the circle center to the label center with the ellipse edge.
  // All inputs and the result are in pixels relative to the page canvas.
  const ellipseEdgePoint = (cx, cy, lx, ly) => {
    const dx = lx - cx;
    const dy = ly - cy;
    if (dx === 0 && dy === 0) return { x: cx, y: cy };
    const denom = Math.sqrt((dx / CIRCLE_RX) ** 2 + (dy / CIRCLE_RY) ** 2);
    // Clamp t to <= 1 so we never overshoot the label when it's very close
    const t = Math.min(1 / denom, 1);
    return { x: cx + dx * t, y: cy + dy * t };
  };

  return (
    <div className="pdf-page-wrapper">
      <div
        className="pdf-page-clickable"
        ref={pageRef}
        onClick={handleBackgroundClick}
      >
        <Page
          pageNumber={pageNumber}
          width={PAGE_WIDTH}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={({ height }) => setPageHeight(height)}
        />
        <div className="annotation-layer">
          {/* SVG with a pixel viewBox so we can do exact ellipse-edge math */}
          {pageHeight && (
            <svg
              className="leader-svg"
              viewBox={`0 0 ${PAGE_WIDTH} ${pageHeight}`}
              preserveAspectRatio="none"
            >
              {annotations.map(ann => {
                const cx = ann.x * PAGE_WIDTH;
                const cy = ann.y * pageHeight;
                const lx = (ann.x + (ann.labelDx ?? 0.08)) * PAGE_WIDTH;
                const ly = (ann.y + (ann.labelDy ?? 0)) * pageHeight;
                const start = ellipseEdgePoint(cx, cy, lx, ly);
                return (
                  <line
                    key={`leader-${ann.id}`}
                    x1={start.x}
                    y1={start.y}
                    x2={lx}
                    y2={ly}
                    stroke="#ef4444"
                    strokeWidth="2.5"
                  />
                );
              })}
            </svg>
          )}

          {annotations.map(ann => (
            <Annotation
              key={ann.id}
              ann={ann}
              pageRef={pageRef}
              onUpdate={(patch) => onUpdateAnnotation(ann.id, patch)}
              onDelete={() => onDeleteAnnotation(ann.id)}
            />
          ))}

          {pending && (
            <PendingPopover
              pending={pending}
              labelOptions={labelOptions}
              onPick={onPickLabel}
              onCancel={onCancelPending}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Annotation({ ann, pageRef, onUpdate, onDelete }) {
  const labelDx = ann.labelDx ?? 0.08;
  const labelDy = ann.labelDy ?? 0;
  const dragState = useRef(null);

  const onLabelMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = pageRef.current.getBoundingClientRect();
    dragState.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startDx: labelDx,
      startDy: labelDy,
      pageWidth: rect.width,
      pageHeight: rect.height,
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    const s = dragState.current;
    if (!s) return;
    const dx = s.startDx + (e.clientX - s.startClientX) / s.pageWidth;
    const dy = s.startDy + (e.clientY - s.startClientY) / s.pageHeight;
    onUpdate({ labelDx: dx, labelDy: dy });
  };

  const onMouseUp = () => {
    dragState.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  return (
    <>
      <div
        className="annotation-circle"
        style={{ left: `${ann.x * 100}%`, top: `${ann.y * 100}%` }}
      />
      <div
        className="annotation-label"
        style={{ left: `${(ann.x + labelDx) * 100}%`, top: `${(ann.y + labelDy) * 100}%` }}
        onMouseDown={onLabelMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        {ann.label}
        <button
          className="annotation-delete"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Remove annotation"
        >
          ×
        </button>
      </div>
    </>
  );
}

function PendingPopover({ pending, labelOptions, onPick, onCancel }) {
  // Flip the menu to the left of the circle if the click is past the middle
  const flip = pending.x > 0.55;
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const customInputRef = useRef(null);

  useEffect(() => {
    if (customMode) {
      // Focus the input when entering custom mode
      setTimeout(() => customInputRef.current?.focus(), 0);
    }
  }, [customMode]);

  const submitCustom = () => {
    const trimmed = customText.trim();
    if (!trimmed) return;
    onPick(trimmed);
  };

  const onCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitCustom();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setCustomMode(false);
    }
  };

  const noPresets = labelOptions.length === 0;

  return (
    <>
      <div
        className="annotation-circle pending"
        style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
      />
      <div
        className={`pending-popover ${flip ? 'flip' : ''}`}
        style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="popover-menu">
          <div className="popover-title">Annotate as...</div>

          {!customMode && (
            <>
              {noPresets && (
                <div className="popover-note">All preset labels for this statement are used. Add a custom one below.</div>
              )}
              {labelOptions.map(opt => (
                <button
                  key={opt.value}
                  className="popover-option"
                  onClick={() => onPick(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
              <button
                className="popover-add-custom"
                onClick={() => setCustomMode(true)}
              >
                + Custom annotation
              </button>
              <button className="popover-cancel" onClick={onCancel}>Cancel</button>
            </>
          )}

          {customMode && (
            <>
              <input
                ref={customInputRef}
                type="text"
                className="popover-custom-input"
                placeholder="Type the annotation text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={onCustomKeyDown}
              />
              <div className="popover-custom-actions">
                <button
                  className="popover-option popover-option-primary"
                  onClick={submitCustom}
                  disabled={!customText.trim()}
                >
                  Add
                </button>
                <button
                  className="popover-cancel"
                  onClick={() => { setCustomMode(false); setCustomText(''); }}
                >
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
